import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useAsyncDebounce } from 'react-table';
import TextareaAutosize from 'react-textarea-autosize';
import clsx from 'clsx';

export interface GrowegyPlainTextEditorHandle {
  insertAtCaret: (text: string) => void;
}

interface GrowegyPlainTextEditorProps {
  setValue: (s: string) => void;
  initialValue: string;
  disabled: boolean;
  readOnly: boolean;
  isInvalid: boolean;
  errorText?: string;
  isError?: boolean;
  className?: string;
  errorClassName?: string;
  debounceMs?: number;
  dataTestPrefix?: string;
  minRows?: number;
  placeholder?: string;
}

export const GrowegyPlainTextEditor = React.forwardRef<
  GrowegyPlainTextEditorHandle,
  GrowegyPlainTextEditorProps
>((props, ref) => {
  const {
    setValue,
    initialValue,
    disabled,
    readOnly,
    isInvalid,
    errorText,
    isError,
    className,
    errorClassName,
    debounceMs,
    dataTestPrefix,
    minRows,
    placeholder,
  } = props;
  const [currentValue, setCurrentValue] = useState(initialValue ?? '');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const onChange = useAsyncDebounce((v) => {
    setValue(v ?? '');
  }, debounceMs ?? 500);

  useEffect(() => {
    setCurrentValue(initialValue || '');
  }, [initialValue]);

  const handleChange = (v: string | null) => {
    setCurrentValue(v ?? '');
    onChange(v);
  };

  const insertAtCaret = (text: string) => {
    if (disabled || readOnly) return;
    const textarea = inputRef.current;
    const selection = (() => {
      if (textarea) {
        const start = textarea.selectionStart ?? currentValue.length;
        const end = textarea.selectionEnd ?? start;
        return { start, end };
      }
      return lastSelectionRef.current ?? { start: currentValue.length, end: currentValue.length };
    })();
    const before = currentValue.slice(0, selection.start);
    const after = currentValue.slice(selection.end);
    const next = `${before}${text}${after}`;
    setCurrentValue(next);
    setValue(next);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      try {
        el.focus();
        const caret = selection.start + text.length;
        el.setSelectionRange(caret, caret);
        lastSelectionRef.current = { start: caret, end: caret };
      } catch {}
    });
  };

  useImperativeHandle(ref, () => ({
    insertAtCaret,
  }));

  return (
    <div
      onKeyDown={(e) => {
        e.stopPropagation();
      }}
      data-test={`${dataTestPrefix}`}
      className={clsx({
        'is-invalid': isInvalid,
        'no-border': readOnly,
      })}
    >
      <>
        <TextareaAutosize
          value={currentValue}
          placeholder={disabled || readOnly ? '' : placeholder}
          disabled={disabled}
          readOnly={readOnly}
          ref={inputRef}
          data-test={dataTestPrefix}
          onChange={(e) => handleChange(e.target.value)}
          onSelect={(e) => {
            const el = e.currentTarget;
            lastSelectionRef.current = { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 };
          }}
          onKeyUp={(e) => {
            const el = e.currentTarget as HTMLTextAreaElement;
            lastSelectionRef.current = { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 };
          }}
          className={clsx('growegy-form__text-area', 'w-100', className, {
            'is-invalid': isInvalid,
          })}
          minRows={minRows}
          onFocus={(e) => {
            requestAnimationFrame(() => {
              e.target?.setSelectionRange(
                lastSelectionRef.current?.start ?? 0,
                lastSelectionRef.current?.end ?? 0
              );
            });
          }}
        />
        {isError && errorText && (
          <div
            className={errorClassName ?? 'invalid-feedback'}
            data-test={`${dataTestPrefix}-validation-error`}
          >
            {errorText}
          </div>
        )}
      </>
    </div>
  );
});
